import React, { useState } from 'react';
import SocialAuthButtons from './SocialAuthButtons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../hooks/useTheme';

/**
 * Sign In Form Component
 * Implements US-1.1.3C: Traditional Login (Email/Password)
 * Implements US-1.1.3D: Remember Me / Keep Me Logged In
 */
export default function SignInForm({ setAuthMode, onSuccess }) {
  const { login } = useAuth();
  const theme = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (  _e) => {
    _e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await login(formData.email, formData.password, formData.rememberMe);
      // TODO: Handle email verification errors from API
      // - If error.emailVerified === false, show verification message
      // - Provide "Resend verification email" option
      // - Don't allow login until email is verified

      // Call onSuccess callback to close modal
      if (onSuccess) {onSuccess();}
    } catch (err) {
      // TODO: Check if error is due to unverified email
      // Display appropriate message and resend option
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SocialAuthButtons />

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 ${theme.focusRing}`}
            placeholder="john@example.com"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 ${theme.focusRing}`}
            placeholder="••••••••"
            required
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="rememberMe"
              checked={formData.rememberMe}
              onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
              className={`w-4 h-4 rounded ${theme.focusRing}`}
              style={{ accentColor: theme.buttonPrimary.split(' ')[0].replace('bg-', '') }}
            />
            <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700 cursor-pointer">
              Remember me
            </label>
          </div>

          <button
            type="button"
            onClick={() => setAuthMode('forgot')}
            className={`text-sm ${theme.textPrimary} ${theme.textHover} font-medium`}
          >
            Forgot password?
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`w-full ${theme.buttonPrimary} py-3 rounded-lg transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {loading ? 'Signing In...' : 'Sign In'}
        </button>
      </form>

      <div className="mt-6 text-center text-sm text-gray-600">
        Do not have an account?{' '}
        <button
          type="button"
          onClick={() => setAuthMode('signup')}
          className={`${theme.textPrimary} ${theme.textHover} font-semibold`}
        >
          Sign up
        </button>
      </div>
    </>
  );
}
